import { resolveChoiceIdFromStoredAnswer } from './instrumentUtils'

const pickScoresToDisplay = ({ scores = [], metadata = {} }) => {
  const explicitlyFlagged = (scores || []).filter(
    (score) => score?.display_score_to_participant === true,
  )

  if (explicitlyFlagged.length > 0) return explicitlyFlagged

  if (metadata?.display_score_to_participant === true && scores?.length) {
    return scores.slice(0, 1)
  }

  return []
}

const getQuestionScoresByIdAndTotal = ({ questionMap, submittedQuestions }) => {
  const questionScoreById = {}
  let hasAnyScoreValue = false
  let total = 0

  // Check if any answer choice has scoring configured at all.
  Object.values(questionMap || {}).forEach((question = {}) => {
    const { choices = {}, choices_order: choicesOrder = [] } = question
    choicesOrder.forEach((choiceId) => {
      const scoreValue = choices?.[choiceId]?.score_value
      if (scoreValue !== undefined && scoreValue !== null && scoreValue !== '') {
        hasAnyScoreValue = true
      }
    })
  })

  if (!hasAnyScoreValue) return { questionScoreById, total, hasAnyScoreValue }

  ;(submittedQuestions || []).forEach((question = {}) => {
    const questionId = question?.id
    if (!questionId) return

    const sourceQuestion = questionMap?.[questionId]
    if (!sourceQuestion?.choices) return

    const answer = question?.answer?.[0]
    const selectedChoiceId = resolveChoiceIdFromStoredAnswer(
      sourceQuestion.choices,
      answer,
    )
    if (!selectedChoiceId) return

    const selectedChoice = sourceQuestion.choices[selectedChoiceId]
    const scoreValue = selectedChoice?.score_value
    if (scoreValue === undefined || scoreValue === null || scoreValue === '') return

    const numericScore = Number(scoreValue)
    if (!Number.isNaN(numericScore)) {
      questionScoreById[questionId] = numericScore
      total += numericScore
    }
  })

  return { questionScoreById, total, hasAnyScoreValue }
}

const getDomainQuestionIds = (questionsWithScoring = {}, domainId) => {
  if (!questionsWithScoring) return []

  // Structure: [{ domain_id, questions: [...] }]
  if (Array.isArray(questionsWithScoring)) {
    const domainMatch = questionsWithScoring.find(
      (entry) =>
        entry?.domain_id === domainId ||
        entry?.domainId === domainId ||
        entry?.id === domainId,
    )

    if (!domainMatch) return []

    return (
      domainMatch?.questions ||
      domainMatch?.question_ids ||
      domainMatch?.questionIds ||
      []
    )
  }

  // Structure: { questionId: [domainId] } OR { questionId: domainId }
  return Object.keys(questionsWithScoring).filter((questionId) => {
    const mapped = questionsWithScoring?.[questionId]
    return Array.isArray(mapped) ? mapped.includes(domainId) : mapped === domainId
  })
}

const computeDomainValues = ({
  domains = {},
  questionsWithScoring = {},
  questionScoreById = {},
}) => {
  const domainValues = {}

  Object.keys(domains).forEach((domainId) => {
    const op = (domains?.[domainId]?.formula || 'SUM').toUpperCase()
    const questionIds = getDomainQuestionIds(questionsWithScoring, domainId)

    const values = questionIds
      .map((qid) => questionScoreById?.[qid])
      .filter((v) => v !== undefined && v !== null)

    if (!values.length) {
      domainValues[domainId] = 0
      return
    }

    if (op === 'MEAN') {
      domainValues[domainId] = values.reduce((acc, v) => acc + v, 0) / values.length
      return
    }

    if (op === 'PRODUCT') {
      domainValues[domainId] = values.reduce((acc, v) => acc * v, 1)
      return
    }

    // Default SUM
    domainValues[domainId] = values.reduce((acc, v) => acc + v, 0)
  })

  return domainValues
}

const evaluateFormula = ({ formula, domainValues = {} }) => {
  if (typeof formula !== 'string' || !formula.trim()) return null

  // Normalize editor-introduced spacing characters from scoring builder.
  let expression = formula
    .replace(/\u00A0/g, ' ') // non-breaking space
    .replace(/[\u200B-\u200D\uFEFF]/g, '') // zero-width chars

  Object.keys(domainValues).forEach((domainId) => {
    const escaped = domainId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Domain ids can include non-word chars (for example "#a"), so word
    // boundaries are not reliable for replacement.
    expression = expression.replace(
      new RegExp(`(^|[^A-Za-z0-9_])(${escaped})(?=$|[^A-Za-z0-9_])`, 'g'),
      `$1${domainValues[domainId]}`,
    )
  })

  const normalized = expression.replace(/\^/g, '**').replace(/\s/g, '')
  if (!/^[\d.+\-*/()]+$/.test(normalized)) return null

  try {
    // eslint-disable-next-line no-new-func
    const evaluated = Function(`"use strict"; return (${normalized});`)()
    const numeric = Number(evaluated)
    if (Number.isNaN(numeric) || !Number.isFinite(numeric)) return null

    const asString = `${numeric}`
    if (asString.includes('.') && asString.split('.')[1].length > 2) {
      return Number(numeric.toFixed(2))
    }
    return numeric
  } catch (e) {
    return null
  }
}

export const computeParticipantScores = ({ webSurvey, submissionSurvey }) => {
  const questionMap = webSurvey?.questions || {}
  const submittedQuestions = submissionSurvey?.questions || []
  const questionsWithScoring = webSurvey?.questionsWithScoring || {}
  const domains = webSurvey?.domains || {}
  const scores = Array.isArray(webSurvey?.scores) ? webSurvey.scores : []

  const selectedScores = pickScoresToDisplay({
    scores,
    metadata: webSurvey?.metadata,
  })

  if (!selectedScores.length) return []

  const { questionScoreById, total, hasAnyScoreValue } =
    getQuestionScoresByIdAndTotal({ questionMap, submittedQuestions })

  if (!hasAnyScoreValue) return []

  const hasFormulaConfig = domains && Object.keys(domains).length > 0

  if (!hasFormulaConfig) {
    return selectedScores.map((score) => ({
      id: score?.id,
      name: score?.name,
      value: total,
    }))
  }

  const domainValues = computeDomainValues({
    domains,
    questionsWithScoring,
    questionScoreById,
  })

  return selectedScores.map((score) => {
    const valueFromFormula = evaluateFormula({
      formula: score?.formula,
      domainValues,
    })

    return {
      id: score?.id,
      name: score?.name,
      value: valueFromFormula !== null ? valueFromFormula : total,
    }
  })
}

export const computeInstrumentScoreTotal = ({ webSurvey, submissionSurvey }) => {
  const questionMap = webSurvey?.questions || {}
  const submittedQuestions = submissionSurvey?.questions || []
  const questionsWithScoring = webSurvey?.questionsWithScoring || {}
  const domains = webSurvey?.domains || {}
  const scores = Array.isArray(webSurvey?.scores) ? webSurvey.scores : []

  const selectedScores = pickScoresToDisplay({
    scores,
    metadata: webSurvey?.metadata,
  })

  const { questionScoreById, total, hasAnyScoreValue } =
    getQuestionScoresByIdAndTotal({ questionMap, submittedQuestions })

  if (!hasAnyScoreValue) return null

  // If no scoring formula configuration, keep simple sum behavior.
  if (!scores.length || !domains || !Object.keys(domains).length) {
    return total
  }

  // If we couldn't identify a score to display, default to the simple total.
  if (!selectedScores.length) return total

  const hasFormulaConfig = domains && Object.keys(domains).length > 0
  if (!hasFormulaConfig) return total

  const domainValues = computeDomainValues({
    domains,
    questionsWithScoring,
    questionScoreById,
  })

  const firstScore = selectedScores[0]
  const valueFromFormula = evaluateFormula({
    formula: firstScore?.formula,
    domainValues,
  })

  return valueFromFormula !== null ? valueFromFormula : total
}

